import type { RetailerSearchResult } from '@packages/models/api';

import { useAuthState } from '@packages/contexts/auth';
import { useContentSettings } from '@packages/contexts/content-settings';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import { WorksheetAnswerStatus } from '@packages/models/api';
import { Icon, Typography } from '@packages/ui/shared';
import { DashboardBanner } from '@web/components/dashboard-banner';
import { StatCard } from '@web/components/stat-card';
import { DATE_FORMAT } from '@web/config/constants';
import { analyticsService, qmrsService, techlineService } from '@web/services/singletons';
import { formatToTitleCase, getDisplayValue } from '@web/utils';
import { formatDate } from '@web/utils/date-format';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useNavigate } from 'react-router-dom';

import type { CaseManagementTask } from './components/case-management/task-card';

import { onDashboardSyncMessage } from '../../utils/case-tab-sync';
import { CreateCaseModalV2 } from '../components/create-case-modal/v2/create-case-modal-v2';
import { SearchRetailer } from '../components/search-retailer';
import { CaseManagement } from './case-management';

const logger = getLogger('techline');

const TECHLINE_CASES_DEFAULT_PARAMS = {
    myCases: true,
    page: 1,
    recordCount: 10,
    trigger: 'both' as 'both' | 'todo' | 'pending',
};

const useStyles = createUseStyles({
    headerActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
    },
    headerCaseScopeWrapper: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        width: 169,
    },
    headerSearchRetailer: {
        minWidth: 256,
    },
    headerCaseScopeSelect: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 169,
        padding: '8px 16px',
        borderRadius: 4,
        background: '#DEE6EF',
        color: '#1F518F',
        textAlign: 'center',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 15,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '160%',
        border: 'none',
        height: 40,
        cursor: 'pointer',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        paddingRight: 36,
        paddingLeft: 16,
        outline: 'none',
    },
    headerCaseScopeIcon: {
        position: 'absolute',
        right: 12,
        marginTop: '1px',
        pointerEvents: 'none',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
    },
    headerCaseScopeDropdown: {
        display: 'flex',
        width: 169,
        padding: '8px 0',
        flexDirection: 'column',
        alignItems: 'flex-start',
        borderRadius: 4,
        background: '#FFFFFF',
        boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.16), 0 8px 24px 0 rgba(0, 0, 0, 0.10)',
        color: '#071424',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 15,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '160%',
        position: 'absolute',
        top: 'calc(100% + 8px)',
        left: 0,
        zIndex: 10,
        overflow: 'hidden',
    },
    headerCaseScopeOption: {
        width: '100%',
        border: 'none',
        background: 'transparent',
        color: 'inherit',
        padding: '8px 16px',
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        fontStyle: 'inherit',
        fontWeight: 600,
        lineHeight: 'inherit',
        outline: 'none',
        transition: 'background 0.2s ease, color 0.2s ease',
        '&:hover': {
            background: '#DEE6EF',
            color: '#1F518F',
        },
        '&:focus-visible': {
            background: '#DEE6EF',
            color: '#1F518F',
        },
    },
    headerCaseScopeOptionSelected: {
        background: '#DEE6EF',
        color: '#1F518F',
    },
    statCardsSection: {
        display: 'grid',
        gap: 24,
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        alignItems: 'stretch',
        '@media (max-width: 1280px)': {
            gap: 20,
        },
        '@media (max-width: 1200px)': {
            gap: 16,
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        },
        '@media (max-width: 992px)': {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 16,
        },
        '@media (max-width: 640px)': {
            gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
            gap: 12,
        },
    },
    statCard: {
        width: '100%',
    },
});

type TechlineDashboardCase = {
    carline?: string;
    caseNumber: string;
    modifiedOn?: string;
    repairCode?: string;
    repairOrderNumber?: string;
    techlineCaseStatus: string;
    vin?: string;
    // Backend historically returned worksheet titles as strings; the newer payload returns
    // objects that also carry the submission status. Both shapes are accepted so the client
    // works whether or not the backend change is deployed.
    worksheets?: Array<TechlineDashboardWorksheet | string>;
};

type TechlineDashboardCasesResponse = {
    pendingCases?: TechlineDashboardLane;
    stats?: Record<string, any>;
    todoCases?: TechlineDashboardLane;
};

type TechlineDashboardLane = {
    cases?: TechlineDashboardCase[];
    endCount?: number;
    startCount?: number;
    totalCount?: number;
};

type TechlineDashboardWorksheet = {
    status?: string;
    worksheetTitle?: string;
};

const DEFAULT_ACTION_LABEL = 'Review & Update';
const REVIEW_CLOSE_ACTION_LABEL = 'Review & Close';

const CASE_SCOPE_OPTIONS: Array<{ value: 'my' | 'all'; label: string }> = [
    { value: 'my', label: 'My cases' },
    { value: 'all', label: 'All retailer cases' },
];

type CaseLane = 'todo' | 'pending';
type CaseLoadTrigger = 'todo' | 'pending' | 'both';

type LanePaginationState = {
    endCount: number;
    hasMore: boolean;
    page: number;
    totalCount: number;
};

const createInitialPaginationState = (): LanePaginationState => ({
    totalCount: 0,
    endCount: 0,
    page: 0,
    hasMore: false,
});

const createInitialTasksState = () => ({
    todo: [] as CaseManagementTask[],
    pendingTechlineResponse: [] as CaseManagementTask[],
});

function formatAverageResponseTimeLabel(value?: string | null): string | null {
    if (!value) {
        return null;
    }

    const hoursMatch = value.match(/(\d+)\s*hour/i);
    const minutesMatch = value.match(/(\d+)\s*minute/i);

    const parts: string[] = [];

    if (hoursMatch) {
        const hours = Number(hoursMatch[1]);
        if (!Number.isNaN(hours)) {
            parts.push(`${hours} ${hours === 1 ? 'hr' : 'hrs'}`);
        }
    }

    if (minutesMatch) {
        const minutes = Number(minutesMatch[1]);
        if (!Number.isNaN(minutes)) {
            parts.push(`${minutes} ${minutes === 1 ? 'mi' : 'mns'}`);
        }
    }

    if (parts.length > 0) {
        return parts.join(' ');
    }

    return value;
}

function formatLastUpdated(date?: string): string {
    if (!date) {
        return '-';
    }

    try {
        return formatDate(date, DATE_FORMAT.MON_DAY_YEAR_AT_H_MM);
    } catch (error) {
        logger.warn('Failed to format Techline dashboard case date', { error, caseDate: date });
        return '-';
    }
}

function formatStatusLabel(status?: string): string | undefined {
    if (!status) {
        return undefined;
    }

    return formatToTitleCase(status);
}

function getCaseTitle(techlineCase: TechlineDashboardCase): string {
    const vinLabel = getVinLastEightCharacters(techlineCase.vin);
    const carlineLabel =
        sanitizeCarlineLabel(techlineCase.carline) ??
        sanitizeCarlineLabel(getWorksheetTitle(techlineCase.worksheets?.[0]));

    if (vinLabel && carlineLabel) {
        return `${vinLabel} (${carlineLabel})`;
    }

    if (vinLabel) {
        return vinLabel;
    }

    if (carlineLabel) {
        return carlineLabel;
    }

    return techlineCase.caseNumber;
}

function getVinLastEightCharacters(vin?: string | null): string | undefined {
    if (!vin) {
        return undefined;
    }

    const normalizedVin = vin.trim().toUpperCase();

    if (!normalizedVin) {
        return undefined;
    }

    if (normalizedVin.length <= 8) {
        return normalizedVin;
    }

    return normalizedVin.slice(-8);
}

// Read a worksheet's submission status, tolerating both the legacy string payload
// (no status available) and the newer object payload.
function getWorksheetStatus(worksheet: TechlineDashboardWorksheet | string | undefined): string | undefined {
    return typeof worksheet === 'string' ? undefined : worksheet?.status;
}

// Read a worksheet's title, tolerating both the legacy string payload and the newer object payload.
function getWorksheetTitle(worksheet: TechlineDashboardWorksheet | string | undefined): string | undefined {
    return typeof worksheet === 'string' ? worksheet : worksheet?.worksheetTitle;
}

function sanitizeCarlineLabel(label?: string | null): string | undefined {
    if (!label) {
        return undefined;
    }

    const trimmed = label.trim();

    if (!trimmed) {
        return undefined;
    }

    const withoutYearSuffix = trimmed
        .replace(/\s+['’]\d{2,4}$/u, '')
        .replace(/\s+MY\d{2,4}$/iu, '')
        .trim();

    return withoutYearSuffix || trimmed;
}

const AUTO_CLOSE_DAYS = 14;

export function DashboardV2View() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { state: dashboardState } = useContentSettings();
    const { account } = useAuthState();
    const classes = useStyles();
    const [showCreateCaseModal, setShowCreateCaseModal] = useState(false);
    const [dashboardStatsData, setDashboardStatsData] = useState<any>(null);
    const [isLoadingDashboardStats, setIsLoadingDashboardStats] = useState(false);
    const [caseManagementSummary, setCaseManagementSummary] = useState<{
        todoCount: number;
        pendingTechlineResponseCount: number;
    } | null>(null);
    const [isCaseManagementLoading, setIsCaseManagementLoading] = useState(true);
    const [caseManagementTasks, setCaseManagementTasks] = useState(createInitialTasksState);
    const [casePagination, setCasePagination] = useState({
        todo: createInitialPaginationState(),
        pending: createInitialPaginationState(),
    });
    const techlineContents = dashboardState.dashboardData?.techline || [];
    const bannerData = techlineContents.filter((content) => content.contentType === 'BANNER')?.[0] || {};
    const { abortSignalRef, signalAbort } = useAbortController();
    const [allRetailers, setAllRetailers] = useState<RetailerSearchResult[] | any[]>([]);
    const [selectedRetailer, setSelectedRetailer] = useState<RetailerSearchResult[]>([]);
    const [caseScope, setCaseScope] = useState<'my' | 'all'>('my');
    const [isCaseScopeMenuOpen, setIsCaseScopeMenuOpen] = useState(false);
    const caseScopeMenuRef = useRef<HTMLDivElement | null>(null);
    const caseScopeButtonRef = useRef<HTMLButtonElement | null>(null);
    const loadingLanesRef = React.useRef<{ todo: boolean; pending: boolean; both: boolean }>({
        todo: false,
        pending: false,
        both: false,
    });
    const activeCaseRequestsRef = React.useRef(0);
    const [isNonRetailer, setIsNonRetailer] = useState(false);

    function handleBannerButtonClick() {
        window.open(bannerData.link, '_blank');
    }

    const fetchSearchHistory = useCallback(async () => {
        try {
            const response = await techlineService.recentSearch(false, abortSignalRef.current);

            if (!response.success || !response.data?.retailerSearch?.length) {
                return;
            }

            const retailerOptions = response.data.retailerSearch.map((retailer: { name: string; code: string }) => ({
                retailerName: retailer.name,
                retailerNumber: retailer.code,
            }));

            setAllRetailers(retailerOptions);
        } catch (error) {
            logger.error('Failed to fetch recent retailer searches', { error });
        }
    }, [abortSignalRef]);

    const clearSelection = useCallback(async () => {
        signalAbort();
        setSelectedRetailer([]);
        setAllRetailers([]);
        setDashboardStatsData(null);
        setCaseManagementSummary(null);
        setCaseManagementTasks(createInitialTasksState());
        setCasePagination({
            todo: createInitialPaginationState(),
            pending: createInitialPaginationState(),
        });
        setIsCaseManagementLoading(false);
        setIsLoadingDashboardStats(false);

        await fetchSearchHistory();
    }, [fetchSearchHistory, signalAbort]);

    const fetchAllRetailers = useCallback(
        async (query: string) => {
            const sanitizedQuery = query.replace(/\s/g, '');

            if (!sanitizedQuery.length) {
                await clearSelection();
                return;
            }

            try {
                if (isNonRetailer) {
                    const response = await qmrsService.fetchRetailers({
                        searchParams: { query, canCreateQmr: true },
                        signal: abortSignalRef.current,
                        ignoreCache: true,
                    });

                    if (!response.success && response.aborted) {
                        return;
                    } else if (!response.success) {
                        throw response.data;
                    }

                    if (response.data.retailers.length) {
                        setAllRetailers(
                            response.data.retailers.map((d) => ({
                                retailerName: d.name,
                                retailerNumber: d.code,
                            }))
                        );
                    }
                }
            } catch (error) {
                logger.error('Failed to search retailers', { error });
            }
        },
        [abortSignalRef, clearSelection, isNonRetailer]
    );

    const handleSetRetailer = useCallback((selected: RetailerSearchResult[]) => {
        setSelectedRetailer(selected);
    }, []);

    const handleCaseScopeChange = useCallback(
        (nextValue: 'my' | 'all') => {
            if (nextValue === caseScope) {
                return;
            }

            signalAbort();
            setCaseScope(nextValue);
        },
        [caseScope, signalAbort]
    );

    const toggleCaseScopeMenu = useCallback(() => {
        setIsCaseScopeMenuOpen((prev) => !prev);
    }, []);

    const closeCaseScopeMenu = useCallback((focusButton: boolean = false) => {
        setIsCaseScopeMenuOpen(false);
        if (focusButton && caseScopeButtonRef.current) {
            caseScopeButtonRef.current.focus();
        }
    }, []);

    const handleCaseScopeOptionSelect = useCallback(
        (value: 'my' | 'all') => {
            handleCaseScopeChange(value);
            closeCaseScopeMenu(true);
        },
        [closeCaseScopeMenu, handleCaseScopeChange]
    );

    const fetchInitialRetailers = useCallback(async () => {
        try {
            const response = await techlineService.retrieveRetailer(false, abortSignalRef.current);

            if (!response.success || !response.data?.results?.length) {
                setIsNonRetailer(true);
                await fetchSearchHistory();
                setIsCaseManagementLoading(false);
                return;
            }

            const retailers = response.data.results;

            setAllRetailers(retailers);
            setIsCaseManagementLoading(false);
        } catch (error) {
            logger.error('Failed to load retailers', { error });
            setIsCaseManagementLoading(false);
        }
    }, [abortSignalRef, fetchSearchHistory]);

    useEffect(() => {
        if (!isCaseScopeMenuOpen) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (caseScopeMenuRef.current && !caseScopeMenuRef.current.contains(event.target as Node)) {
                closeCaseScopeMenu();
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeCaseScopeMenu(true);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [closeCaseScopeMenu, isCaseScopeMenuOpen]);

    useEffect(() => {
        fetchInitialRetailers();

        return () => {
            signalAbort();
        };
    }, [fetchInitialRetailers, signalAbort]);

    const fetchDashboardMetrics = useCallback(
        async ({ retailerNumber, myCases }: { retailerNumber: string; myCases: boolean }) => {
            try {
                const response = await techlineService.getDashboardMetrics({
                    retailerNumber,
                    myCases,
                    ignoreCache: true,
                    signal: abortSignalRef.current,
                });

                if (!response.success) {
                    if (!response.aborted) {
                        logger.error(`Failed to fetch Techline dashboard metrics [${retailerNumber}]`, {
                            retailerNumber,
                            myCases,
                            error: response.data,
                        });
                        setDashboardStatsData(null);
                    }
                    return;
                }

                const metricsDataRaw = response.data?.metrics ?? response.data ?? null;

                if (!metricsDataRaw) {
                    setDashboardStatsData(null);
                    return;
                }

                const metricsData = {
                    activeCases: metricsDataRaw.activeCases ?? null,
                    pendingActions: metricsDataRaw.pendingActions ?? null,
                    averageResponseTime: metricsDataRaw.averageResponseTime ?? null,
                    closedCasesPercent: metricsDataRaw.closedCasesPercent ?? null,
                    autoClosedCasesPercent: metricsDataRaw.autoClosedCasesPercent ?? null,
                };

                setDashboardStatsData(metricsData);
            } catch (error) {
                if (error instanceof Error && error.name === 'AbortError') {
                    return;
                }

                logger.error(`Failed to fetch Techline dashboard metrics [${retailerNumber}]`, {
                    retailerNumber,
                    myCases,
                    error,
                });
                setDashboardStatsData(null);
            }
        },
        [abortSignalRef]
    );

    const fetchDashboardCases = useCallback(
        async ({
            retailerNumber,
            trigger,
            page,
            append,
            myCases,
        }: {
            retailerNumber: string;
            trigger: CaseLoadTrigger;
            page: number;
            append: boolean;
            myCases: boolean;
        }) => {
            const triggerKey = trigger === 'both' ? 'both' : trigger;

            if (loadingLanesRef.current[triggerKey]) {
                return;
            }

            loadingLanesRef.current[triggerKey] = true;
            activeCaseRequestsRef.current += 1;
            setIsCaseManagementLoading(true);

            try {
                const response = await techlineService.getDashboardCases({
                    retailerNumber,
                    ...TECHLINE_CASES_DEFAULT_PARAMS,
                    myCases,
                    trigger,
                    page,
                    signal: abortSignalRef.current,
                    ignoreCache: true,
                });

                if (!response.success) {
                    if (!response.aborted) {
                        logger.error(`Failed to fetch Techline dashboard cases [${retailerNumber}]`, {
                            retailerNumber,
                            myCases,
                            error: response.data,
                        });
                        if (!append) {
                            setDashboardStatsData(null);
                            setCaseManagementTasks(createInitialTasksState());
                            setCasePagination({
                                todo: createInitialPaginationState(),
                                pending: createInitialPaginationState(),
                            });
                            setCaseManagementSummary({ todoCount: 0, pendingTechlineResponseCount: 0 });
                        }
                    }
                    return;
                }

                logger.debug(`Fetched Techline dashboard cases [${retailerNumber}]`, {
                    retailerNumber,
                    trigger,
                    page,
                    myCases,
                });

                const data = response.data as TechlineDashboardCasesResponse;
                const todoResponse = data?.todoCases;
                const pendingResponse = data?.pendingCases;

                let updatedTodoTasks: CaseManagementTask[] = [];
                let updatedPendingTasks: CaseManagementTask[] = [];
                let updatedTodoPagination: LanePaginationState = createInitialPaginationState();
                let updatedPendingPagination: LanePaginationState = createInitialPaginationState();

                setCaseManagementTasks((prev) => {
                    const baseTodo = append ? prev.todo : [];
                    const basePending = append ? prev.pendingTechlineResponse : [];

                    const shouldUpdateTodo = trigger !== 'pending';
                    const shouldUpdatePending = trigger !== 'todo';

                    const nextTodoTasks = shouldUpdateTodo
                        ? todoResponse
                            ? append
                                ? [...baseTodo, ...mapCasesToTasks(todoResponse.cases, baseTodo.length)]
                                : mapCasesToTasks(todoResponse.cases)
                            : append
                              ? baseTodo
                              : []
                        : baseTodo;

                    const nextPendingTasks = shouldUpdatePending
                        ? pendingResponse
                            ? append
                                ? [...basePending, ...mapCasesToTasks(pendingResponse.cases, basePending.length)]
                                : mapCasesToTasks(pendingResponse.cases)
                            : append
                              ? basePending
                              : []
                        : basePending;

                    updatedTodoTasks = nextTodoTasks;
                    updatedPendingTasks = nextPendingTasks;

                    return {
                        todo: nextTodoTasks,
                        pendingTechlineResponse: nextPendingTasks,
                    };
                });

                setCasePagination((prev) => {
                    const shouldUpdateTodo = trigger !== 'pending';
                    const shouldUpdatePending = trigger !== 'todo';

                    const nextTodoPagination = shouldUpdateTodo
                        ? todoResponse
                            ? {
                                  totalCount:
                                      todoResponse.totalCount ??
                                      (append ? prev.todo.totalCount : (todoResponse.cases?.length ?? 0)),
                                  endCount:
                                      todoResponse.endCount ??
                                      (append ? prev.todo.endCount : (todoResponse.cases?.length ?? 0)),
                                  page,
                                  hasMore:
                                      (todoResponse.endCount ??
                                          (append ? prev.todo.endCount : (todoResponse.cases?.length ?? 0))) <
                                      (todoResponse.totalCount ??
                                          (append ? prev.todo.totalCount : (todoResponse.cases?.length ?? 0))),
                              }
                            : append
                              ? prev.todo
                              : createInitialPaginationState()
                        : prev.todo;

                    const nextPendingPagination = shouldUpdatePending
                        ? pendingResponse
                            ? {
                                  totalCount:
                                      pendingResponse.totalCount ??
                                      (append ? prev.pending.totalCount : (pendingResponse.cases?.length ?? 0)),
                                  endCount:
                                      pendingResponse.endCount ??
                                      (append ? prev.pending.endCount : (pendingResponse.cases?.length ?? 0)),
                                  page,
                                  hasMore:
                                      (pendingResponse.endCount ??
                                          (append ? prev.pending.endCount : (pendingResponse.cases?.length ?? 0))) <
                                      (pendingResponse.totalCount ??
                                          (append ? prev.pending.totalCount : (pendingResponse.cases?.length ?? 0))),
                              }
                            : append
                              ? prev.pending
                              : createInitialPaginationState()
                        : prev.pending;

                    updatedTodoPagination = nextTodoPagination;
                    updatedPendingPagination = nextPendingPagination;

                    return {
                        todo: nextTodoPagination,
                        pending: nextPendingPagination,
                    };
                });

                setCaseManagementSummary({
                    todoCount: updatedTodoPagination.totalCount,
                    pendingTechlineResponseCount: updatedPendingPagination.totalCount,
                });

                if (data?.stats) {
                    setDashboardStatsData(data.stats);
                }
            } catch (error) {
                if (error instanceof Error && error.name === 'AbortError') {
                    return;
                }

                logger.error(`Failed to fetch Techline dashboard cases [${retailerNumber}]`, {
                    retailerNumber,
                    myCases,
                    error,
                });

                if (!append) {
                    setDashboardStatsData(null);
                    setCaseManagementTasks(createInitialTasksState());
                    setCasePagination({
                        todo: createInitialPaginationState(),
                        pending: createInitialPaginationState(),
                    });
                    setCaseManagementSummary({ todoCount: 0, pendingTechlineResponseCount: 0 });
                }
            } finally {
                loadingLanesRef.current[triggerKey] = false;
                activeCaseRequestsRef.current = Math.max(0, activeCaseRequestsRef.current - 1);

                if (
                    activeCaseRequestsRef.current === 0 &&
                    !loadingLanesRef.current.todo &&
                    !loadingLanesRef.current.pending &&
                    !loadingLanesRef.current.both
                ) {
                    setIsCaseManagementLoading(false);
                }
            }
        },
        [abortSignalRef]
    );

    const retailerNumber = selectedRetailer[0]?.retailerNumber;
    const hasRetailerUserRole = Array.isArray(account?.profiles)
        ? account.profiles.some(
              (profile) => profile?.roleId === 'RETAILER_USER' || profile?.roleId === 'RETAILER_ADMIN'
          )
        : false;
    const isDSQM = Array.isArray(account?.baseRoleIds) && account.baseRoleIds.includes('DSQM');
    const effectiveCaseScope = hasRetailerUserRole ? caseScope : 'all';
    const isMyCasesSelected = effectiveCaseScope === 'my';
    const currentCaseScopeLabel =
        CASE_SCOPE_OPTIONS.find((option) => option.value === effectiveCaseScope)?.label ??
        CASE_SCOPE_OPTIONS[0]?.label ??
        'My cases';
    const currentCaseScopeIconName = isCaseScopeMenuOpen ? 'arrow-small-up' : 'arrow-small-down';
    const shouldShowCaseScope = Boolean(hasRetailerUserRole);

    useEffect(() => {
        if (!shouldShowCaseScope) {
            closeCaseScopeMenu();
        }
    }, [closeCaseScopeMenu, shouldShowCaseScope]);

    useEffect(() => {
        if (!hasRetailerUserRole && caseScope !== 'my') {
            setCaseScope('my');
        }
    }, [caseScope, hasRetailerUserRole]);

    useEffect(() => {
        let isMounted = true;

        if (!retailerNumber) {
            setDashboardStatsData(null);
            setCaseManagementSummary({ todoCount: 0, pendingTechlineResponseCount: 0 });
            setCaseManagementTasks(createInitialTasksState());
            setCasePagination({
                todo: createInitialPaginationState(),
                pending: createInitialPaginationState(),
            });
            setIsLoadingDashboardStats(false);

            return () => {
                isMounted = false;
            };
        }

        setIsLoadingDashboardStats(true);
        setDashboardStatsData(null);
        setCaseManagementTasks(createInitialTasksState());
        setCasePagination({
            todo: createInitialPaginationState(),
            pending: createInitialPaginationState(),
        });

        fetchDashboardMetrics({ retailerNumber, myCases: isMyCasesSelected }).finally(() => {
            if (!isMounted) {
                return;
            }
            setIsLoadingDashboardStats(false);
        });

        fetchDashboardCases({
            retailerNumber,
            trigger: 'both',
            page: 1,
            append: false,
            myCases: isMyCasesSelected,
        }).catch((error) => {
            if (error instanceof Error && error.name === 'AbortError') {
                return;
            }

            logger.error('Failed to fetch Techline dashboard cases', { error });
        });

        return () => {
            isMounted = false;
        };
    }, [fetchDashboardCases, fetchDashboardMetrics, retailerNumber, isMyCasesSelected]);

    // Listen for case status changes from child tabs and refresh dashboard data on tab focus
    const isStaleRef = React.useRef(false);

    useEffect(() => {
        const cleanup = onDashboardSyncMessage(() => {
            isStaleRef.current = true;
        });

        return cleanup;
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible' || !isStaleRef.current) {
                return;
            }

            isStaleRef.current = false;

            const currentRetailerNumber = selectedRetailer[0]?.retailerNumber;
            if (!currentRetailerNumber) {
                return;
            }

            signalAbort();

            setIsLoadingDashboardStats(true);
            setDashboardStatsData(null);
            setCaseManagementTasks(createInitialTasksState());
            setCasePagination({
                todo: createInitialPaginationState(),
                pending: createInitialPaginationState(),
            });

            fetchDashboardMetrics({ retailerNumber: currentRetailerNumber, myCases: isMyCasesSelected }).finally(() => {
                setIsLoadingDashboardStats(false);
            });

            fetchDashboardCases({
                retailerNumber: currentRetailerNumber,
                trigger: 'both',
                page: 1,
                append: false,
                myCases: isMyCasesSelected,
            }).catch((error) => {
                if (error instanceof Error && error.name === 'AbortError') {
                    return;
                }
                logger.warn('Failed to refresh Techline dashboard after case status change', { error });
            });
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [fetchDashboardCases, fetchDashboardMetrics, isMyCasesSelected, selectedRetailer, signalAbort]);

    const handleLoadMore = useCallback(
        (trigger: CaseLoadTrigger) => {
            const currentRetailerNumber = selectedRetailer[0]?.retailerNumber;
            if (!currentRetailerNumber) {
                return;
            }

            const todoState = casePagination.todo;
            const pendingState = casePagination.pending;

            if (trigger === 'both') {
                const todoHasMore = todoState.hasMore;
                const pendingHasMore = pendingState.hasMore;

                if (todoHasMore && pendingHasMore && todoState.page === pendingState.page) {
                    const nextPage = todoState.page + 1;
                    void fetchDashboardCases({
                        retailerNumber: currentRetailerNumber,
                        trigger: 'both',
                        page: nextPage,
                        append: true,
                        myCases: isMyCasesSelected,
                    });
                    return;
                }

                if (todoHasMore) {
                    void fetchDashboardCases({
                        retailerNumber: currentRetailerNumber,
                        trigger: 'todo',
                        page: todoState.page + 1,
                        append: true,
                        myCases: isMyCasesSelected,
                    });
                }

                if (pendingHasMore) {
                    void fetchDashboardCases({
                        retailerNumber: currentRetailerNumber,
                        trigger: 'pending',
                        page: pendingState.page + 1,
                        append: true,
                        myCases: isMyCasesSelected,
                    });
                }
                return;
            }

            if (trigger === 'todo' && todoState.hasMore) {
                void fetchDashboardCases({
                    retailerNumber: currentRetailerNumber,
                    trigger: 'todo',
                    page: todoState.page + 1,
                    append: true,
                    myCases: isMyCasesSelected,
                });
            }

            if (trigger === 'pending' && pendingState.hasMore) {
                void fetchDashboardCases({
                    retailerNumber: currentRetailerNumber,
                    trigger: 'pending',
                    page: pendingState.page + 1,
                    append: true,
                    myCases: isMyCasesSelected,
                });
            }
        },
        [casePagination, fetchDashboardCases, isMyCasesSelected, selectedRetailer]
    );

    const canCreateCase = Boolean(account?.techlineCapabilities?.canCreateDLTechlineCase);

    const handleStartNewCase = useCallback(() => {
        setShowCreateCaseModal(true);
        analyticsService.logEvent(ANALYTICS_EVENTS.USER_CLICKS_CREATE_A_TECHLINE_CASE);
    }, []);

    const handleCreateCaseModalHide = useCallback(
        (identifier: string | undefined) => {
            setShowCreateCaseModal(false);
            if (identifier) {
                navigate('/techline/' + identifier);
            }
        },
        [navigate]
    );

    const isStatsLoading = isLoadingDashboardStats && Boolean(retailerNumber);

    return (
        <div className="p-6">
            <div className="d-flex justify-content-between align-items-center pb-6">
                <Typography style={{ lineHeight: 40 }} variant="h2">
                    {t('techline:dashboard.title')}
                </Typography>
                <div className={classes.headerActions}>
                    {shouldShowCaseScope && (
                        <div className={classes.headerCaseScopeWrapper} ref={caseScopeMenuRef}>
                            <button
                                aria-controls="case-scope-menu"
                                aria-expanded={isCaseScopeMenuOpen}
                                aria-haspopup="listbox"
                                className={classes.headerCaseScopeSelect}
                                onClick={toggleCaseScopeMenu}
                                ref={caseScopeButtonRef}
                                type="button"
                            >
                                {currentCaseScopeLabel}
                            </button>
                            <div className={classes.headerCaseScopeIcon}>
                                <Icon color="blueOne" name={currentCaseScopeIconName} size={28} />
                            </div>
                            {isCaseScopeMenuOpen && (
                                <div
                                    aria-activedescendant={`case-scope-option-${caseScope}`}
                                    className={classes.headerCaseScopeDropdown}
                                    id="case-scope-menu"
                                    role="listbox"
                                >
                                    {CASE_SCOPE_OPTIONS.map((option) => {
                                        const isSelected = option.value === caseScope;
                                        return (
                                            <button
                                                aria-selected={isSelected}
                                                className={`${classes.headerCaseScopeOption}${
                                                    isSelected ? ` ${classes.headerCaseScopeOptionSelected}` : ''
                                                }`}
                                                id={`case-scope-option-${option.value}`}
                                                key={option.value}
                                                onClick={() => handleCaseScopeOptionSelect(option.value)}
                                                role="option"
                                                type="button"
                                            >
                                                {option.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                    <SearchRetailer
                        allRetailers={allRetailers}
                        clearSelection={clearSelection}
                        containerClassName={classes.headerSearchRetailer}
                        fetchRetailers={fetchAllRetailers}
                        placeholder="Select Retailer"
                        recentSearch={fetchSearchHistory}
                        selected={selectedRetailer}
                        setSelected={handleSetRetailer}
                    />
                </div>
            </div>
            {bannerData.techlineDashboardId && (
                <div className="pb-6">
                    <DashboardBanner
                        description={bannerData.description}
                        iconName="info-solid"
                        onPress={handleBannerButtonClick}
                        showButton
                        title={bannerData.title}
                    />
                </div>
            )}

            <div className={`${classes.statCardsSection} pb-6`}>
                <StatCard
                    captions={{ text: 'techline:dashboard.statCards.activeCases.caption' }}
                    className={classes.statCard}
                    isLoading={isStatsLoading}
                    title={'techline:dashboard.statCards.activeCases.title'}
                    value={getDisplayValue(dashboardStatsData?.activeCases, '', '-')}
                />
                <StatCard
                    captions={{ text: 'techline:dashboard.statCards.pendingActions.caption' }}
                    className={classes.statCard}
                    isLoading={isStatsLoading}
                    title={'techline:dashboard.statCards.pendingActions.title'}
                    value={getDisplayValue(dashboardStatsData?.pendingActions, '', '-')}
                />
                {/* <StatCard
                    captions={{ text: 'techline:dashboard.statCards.averageResponseTime.caption.data' }}
                    className={classes.statCard}
                    isLoading={isStatsLoading}
                    title={'techline:dashboard.statCards.averageResponseTime.title'}
                    value={getDisplayValue(
                        formatAverageResponseTimeLabel(dashboardStatsData?.averageResponseTime),
                        '',
                        '-'
                    )}
                /> */}
                <StatCard
                    captions={[
                        {
                            iconName: 'checkmark',
                            layout: 'right',
                            showIcon: true,
                            text: 'techline:dashboard.statCards.caseResolutions.caption.closedCases',
                            textVariant: 'default',
                            type: 'decrease',
                            value: getDisplayValue(dashboardStatsData?.closedCasesPercent, '%', '0%'),
                        },
                        {
                            iconName: 'x-close',
                            layout: 'right',
                            showIcon: true,
                            text: 'techline:dashboard.statCards.caseResolutions.caption.autoClosedCases',
                            textVariant: 'default',
                            type: 'increase',
                            value: getDisplayValue(dashboardStatsData?.autoClosedCasesPercent, '%', '0%'),
                        },
                    ]}
                    captionsMarginTop={20}
                    className={classes.statCard}
                    isLoading={isStatsLoading}
                    title={'techline:dashboard.statCards.caseResolutions.title'}
                />
            </div>

            <div className="row g-4 pb-6">
                <CaseManagement
                    hasMorePending={casePagination.pending.hasMore}
                    hasMoreTodo={casePagination.todo.hasMore}
                    hideCardActions={isDSQM}
                    isLoading={isCaseManagementLoading}
                    onLoadMore={handleLoadMore}
                    onStartNewCase={canCreateCase ? handleStartNewCase : undefined}
                    pendingTechlineResponseCount={caseManagementSummary?.pendingTechlineResponseCount ?? null}
                    pendingTechlineResponseTasks={caseManagementTasks.pendingTechlineResponse}
                    todoCount={caseManagementSummary?.todoCount ?? null}
                    todoTasks={caseManagementTasks.todo}
                />
            </div>

            {showCreateCaseModal && <CreateCaseModalV2 onHide={handleCreateCaseModalHide} show={showCreateCaseModal} />}
        </div>
    );
}

function getAutoCloseDaysRemaining(modifiedOn?: string): number | null {
    if (!modifiedOn) {
        return null;
    }

    try {
        const modifiedDate = new Date(modifiedOn);

        if (Number.isNaN(modifiedDate.getTime())) {
            return null;
        }

        const autoCloseDate = new Date(modifiedDate.getTime() + AUTO_CLOSE_DAYS * 24 * 60 * 60 * 1000);
        const now = new Date();
        const remainingMs = autoCloseDate.getTime() - now.getTime();
        const remainingDays = Math.ceil(remainingMs / (24 * 60 * 60 * 1000));

        return Math.max(remainingDays, 0);
    } catch {
        return null;
    }
}

function mapCasesToTasks(cases: TechlineDashboardCase[] | undefined, offset: number = 0): CaseManagementTask[] {
    if (!cases || cases.length === 0) {
        return [];
    }

    return cases.map((caseItem, index) => {
        const caseUrl = `/techline/${caseItem.caseNumber}`;
        const rawWorksheets = caseItem.worksheets ?? [];
        const worksheets = rawWorksheets
            .map((worksheet) => getWorksheetTitle(worksheet)?.trim())
            .filter((title): title is string => Boolean(title));
        const worksheetsCount = worksheets.length;
        const sanitizedCarline = sanitizeCarlineLabel(caseItem.carline) ?? caseItem.carline ?? undefined;

        const hasRepairCode = Boolean(caseItem.repairCode?.trim());
        // Mirror the case-detail "Close case" enablement: a case is only closable when it has a
        // fail code AND every worksheet is submitted (no drafts). A worksheet only blocks closing
        // when it reports a non-submitted status; legacy payloads carry no status, so they are
        // treated as non-blocking and the prior behavior holds until the backend sends statuses.
        const hasAllWorksheetsSubmitted = rawWorksheets.every((worksheet) => {
            const status = getWorksheetStatus(worksheet);
            return !status || status === WorksheetAnswerStatus.Submitted;
        });
        const canCloseCase = hasRepairCode && hasAllWorksheetsSubmitted;
        const autoCloseDaysRemaining = hasRepairCode ? getAutoCloseDaysRemaining(caseItem.modifiedOn) : null;

        return {
            id: `${caseItem.caseNumber}-${index + offset}`,
            title: getCaseTitle(caseItem),
            status: caseItem.techlineCaseStatus,
            statusLabel: formatStatusLabel(caseItem.techlineCaseStatus),
            roNumber: caseItem.repairOrderNumber ?? '--',
            caseNumber: caseItem.caseNumber,
            caseUrl,
            worksheetsCount,
            worksheets,
            vin: caseItem.vin,
            carline: sanitizedCarline,
            lastUpdatedAt: formatLastUpdated(caseItem.modifiedOn),
            autoCloseDaysRemaining,
            actionLabel: canCloseCase ? REVIEW_CLOSE_ACTION_LABEL : DEFAULT_ACTION_LABEL,
            actionVariant: canCloseCase ? 'success' : 'primary',
            onAction: () => {
                if (typeof window !== 'undefined') {
                    window.open(caseUrl, '_blank', 'noreferrer');
                }
            },
        };
    });
}
